import { Component, DestroyRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UsageRightService } from '@markant-platform/usage-right/data-access';
import { UsageRight, UsageRightPaymentModel, UsageRightState } from '@markant-platform/usage-right/model';
import { DatePipe } from '@angular/common';
import { SnackbarService } from '@markant-platform/shared/snackbar/feature';
import { parseDate, USAGE_RIGHT_FORM, UsageRightFormControls } from './configuration/usage-right-edit.form';
import { MarkantSelectOption } from '@markant/elements-angular';
import { TranslocoService } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'markant-platform-usage-right-edit-modal',
  templateUrl: './usage-right-edit-modal.component.html',
  styleUrls: ['./usage-right-edit-modal.component.scss'],
})
export class UsageRightEditModalComponent implements OnInit {
  @Input({ required: true }) usageRight!: UsageRight;

  @Output() dialogFinished = new EventEmitter<boolean>();

  form = USAGE_RIGHT_FORM();
  isLoading = false;

  readonly usageRightStates = UsageRightState;
  readonly dateFormat = 'dd.MM.yyyy';
  readonly usageRightFormControls = UsageRightFormControls;

  stateOptions = Object.values(UsageRightState).map(state => ({
    value: state,
    representation: this.translocoService.translate('usageRight.stateEnum.' + state),
    disabled:
      state === UsageRightState.SUSPENDED &&
      this.form.controls[this.usageRightFormControls.paymentModel].value === UsageRightPaymentModel.FIXED_PRICE,
  })) as MarkantSelectOption[];

  paymentModelOptions = Object.values(UsageRightPaymentModel).map(model => ({
    value: model,
    representation: this.translocoService.translate('usageRight.paymentModelEnum.' + model),
  })) as MarkantSelectOption[];

  constructor(
    private readonly usageRightService: UsageRightService,
    readonly datePipe: DatePipe,
    private readonly snackbarService: SnackbarService,
    private readonly destroyRef: DestroyRef,
    private readonly translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    this.patchForm(this.usageRight);

    this.updateValidityEndDates(this.usageRight.state);

    if ([UsageRightState.SUSPENDED, UsageRightState.STOPPED].includes(this.usageRight.state)) {
      this.form.controls['validityPeriodStart'].disable();
      this.form.controls['validityPeriodEnd'].disable();
    }
  }

  closeDialog(isSuccessful: boolean): void {
    this.isLoading = false;
    this.form.reset();
    this.dialogFinished.emit(isSuccessful);
  }

  onSave(): void {
    this.isLoading = true;
    this.usageRightService
      .updateUsageRight(this.buildEditedUsageRight())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.closeDialog(true),
        error: () => {
          this.isLoading = false;
          this.snackbarService.showErrorSnackbarWithTransloco('organization.overview.edit.usage-right.error');
        },
      });
  }

  onStateChanged(state: string): void {
    this.updateValidityEndDates(state);

    if (['STOPPED', 'SUSPENDED'].includes(state)) {
      this.form.controls['validityPeriodStart'].disable();
      this.form.controls['validityPeriodEnd'].disable();
      return;
    }

    this.form.controls['validityPeriodStart'].enable();
    this.form.controls['validityPeriodEnd'].enable();
  }

  private patchForm(data: UsageRight): void {
    const { state, paymentModel, boughtAt, validityPeriodStart, validityPeriodEnd } = data;

    this.form.patchValue({
      state,
      paymentModel,
      boughtAt: this.datePipe.transform(boughtAt, this.dateFormat, 'UTC') ?? '',
      validityPeriodStart: this.datePipe.transform(validityPeriodStart, this.dateFormat, 'UTC') ?? '',
      validityPeriodEnd: this.datePipe.transform(validityPeriodEnd, this.dateFormat, 'UTC') ?? '',
    });
  }

  private buildEditedUsageRight(): UsageRight {
    const { controls } = this.form;
    const { state, paymentModel } = this.form.getRawValue();
    const { validityPeriodStart, boughtAt, validityPeriodEnd } = controls;

    return {
      ...this.usageRight,
      state,
      paymentModel: paymentModel,
      boughtAt: this.parseDateIfDirty(boughtAt.dirty, boughtAt.value, 'boughtAt'),
      validityPeriodStart: this.parseDateIfDirty(
        validityPeriodStart.dirty,
        validityPeriodStart.value,
        'validityPeriodStart'
      ),
      validityPeriodEnd: this.getUsageRightValidityPeriodEnd(validityPeriodEnd.dirty, validityPeriodEnd.value, state),
    };
  }

  private getUsageRightValidityPeriodEnd(isDirty: boolean, value: string, stateValue: UsageRightState): Date {
    return stateValue === UsageRightState.SUSPENDED
      ? new Date()
      : this.updateTimeIfMidnight(this.parseDateIfDirty(isDirty, value, 'validityPeriodEnd'));
  }

  /**
   * Parse date if form control has changed returns own value. If not, return specific usage right data.
   */
  private parseDateIfDirty(isDirty: boolean, value: string, key: keyof UsageRight): Date {
    return isDirty ? parseDate(value) : (this.usageRight[key] as Date);
  }

  private updateValidityEndDates(state: string): void {
    if (['SUSPENDED'].includes(state)) {
      this.form.controls['validityPeriodEnd'].patchValue('');
    } else if (['STOPPED'].includes(state)) {
      this.form.controls['validityPeriodEnd'].patchValue(
        `${this.datePipe.transform(new Date(), this.dateFormat, 'UTC')}`
      );
      this.form.controls['validityPeriodEnd'].markAsDirty();
    }
  }

  private updateTimeIfMidnight(date: Date): Date {
    if (date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0 && date.getMilliseconds() === 0) {
      const now = new Date();
      date.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
    }
    return date;
  }
}
